import { useCallback, useEffect, useMemo, useState } from "react";
import { LOW_STORAGE_WARNING_BYTES, nowIso } from "../constants";
import { deleteRecord, ensureSeedData, getMessagesByConversation, listAll, putRecord, putSetting } from "../storage/db";
import { estimateStorage, hasEnoughStorage, requestPersistentStorage } from "../storage/storageEstimate";
import type { AppSettings, Conversation, MediaRecord, MemoryItem, Message, StorageSummary, VoiceProfile } from "../types/app";

export function useAppData() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [media, setMedia] = useState<MediaRecord[]>([]);
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [storage, setStorage] = useState<StorageSummary | null>(null);
  const [error, setError] = useState<string>("");

  const refresh = useCallback(async () => {
    try {
      const seeded = await ensureSeedData();
      setSettings(seeded);
      const [memoryRows, conversationRows, mediaRows, voiceRows, storageSummary] = await Promise.all([
        listAll("memories"),
        listAll("conversations"),
        listAll("media"),
        listAll("voiceProfiles"),
        estimateStorage(),
      ]);
      setMemories(memoryRows.sort((a, b) => Number(b.isPinned) - Number(a.isPinned) || b.updatedAt.localeCompare(a.updatedAt)));
      setConversations(conversationRows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
      setMedia(mediaRows);
      setVoiceProfiles(voiceRows);
      setStorage(storageSummary);
      if (conversationRows[0]) setMessages(await getMessagesByConversation(conversationRows[0].id, 80));
    } catch {
      setError("IndexedDB保存失敗または読み込み失敗です。ブラウザの空き容量や設定を確認してください。");
    }
  }, []);

  useEffect(() => {
    void refresh();
    void requestPersistentStorage();
  }, [refresh]);

  const updateSettings = useCallback(
    async (patch: Partial<AppSettings>) => {
      if (!settings) return;
      const next = { ...settings, ...patch, updatedAt: nowIso() };
      setSettings(next);
      await putSetting(next);
      await refresh();
    },
    [refresh, settings],
  );

  const saveMedia = useCallback(
    async (record: MediaRecord) => {
      const storageCheck = await hasEnoughStorage(record.blob.size, LOW_STORAGE_WARNING_BYTES);
      if (!storageCheck.ok) {
        setError("端末ストレージの残容量が少ないため、保存前にバックアップや不要データの削除をおすすめします。");
      }
      await putRecord("media", record);
      await refresh();
    },
    [refresh],
  );

  const saveMemory = useCallback(
    async (memory: MemoryItem) => {
      await putRecord("memories", memory);
      await refresh();
    },
    [refresh],
  );

  const removeMemory = useCallback(
    async (id: string) => {
      await deleteRecord("memories", id);
      await refresh();
    },
    [refresh],
  );

  const saveVoiceProfile = useCallback(
    async (profile: VoiceProfile) => {
      await putRecord("voiceProfiles", profile);
      await refresh();
    },
    [refresh],
  );

  const activeConversation = useMemo(() => conversations[0], [conversations]);
  const activeVoiceProfile = useMemo(
    () => voiceProfiles.find((item) => item.id === settings?.selectedVoiceProfileId) ?? voiceProfiles[0],
    [settings?.selectedVoiceProfileId, voiceProfiles],
  );
  const activeCharacterMedia = useMemo(
    () => media.find((item) => item.id === settings?.selectedCharacterMediaId && item.type !== "audioSample"),
    [media, settings?.selectedCharacterMediaId],
  );

  return {
    settings,
    memories,
    conversations,
    messages,
    media,
    voiceProfiles,
    storage,
    error,
    activeConversation,
    activeVoiceProfile,
    activeCharacterMedia,
    refresh,
    setMessages,
    setError,
    updateSettings,
    saveMedia,
    saveMemory,
    removeMemory,
    saveVoiceProfile,
  };
}
